import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createTheme, getAllThemesData, getThemeById, updateTheme, duplicateTheme } from './themeApi';
import { ActivityThemeStyles } from '../common/themes/theme';

type Modifier = {
  id: string;
  name: string;
};

export type Theme = {
  _id: string;
  name: string;
  styles: ActivityThemeStyles;
  css: string;
  updatedAt: string;
  modifiedBy: Modifier;
};

export type ThemesData = Omit<Theme, 'css'>[];

export type CreateThemeDto = Pick<Theme, Exclude<keyof Theme, 'updatedAt' | 'modifiedBy' | '_id'>>;

export const useFetchAllThemesData = () => {
  return useQuery<ThemesData>({
    queryKey: ['themes', 'list'],
    queryFn: () => getAllThemesData()
  });
};

/**
 * TODO refactor consumers to use useGetThemeQuery
 */
export const useTheme = (themeId?: string | null, enabled = true) => {
  return useQuery<Theme>({
    queryKey: ['theme', { themeId }],
    queryFn: () => getThemeById(themeId as string),
    enabled: !!themeId && enabled
  });
};

type UseUpdateThemeArgs = {
  onSuccess: () => void;
  onError: () => void;
};

export function useUpdateTheme(props: UseUpdateThemeArgs) {
  const { onSuccess, onError } = props;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTheme,
    onMutate: async updatedTheme => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: ['themes', 'list']
      });
      await queryClient.cancelQueries({
        queryKey: ['theme', { themeId: updatedTheme._id }]
      });

      const previousList = queryClient.getQueryData<ThemesData>(['themes', 'list']);
      const previousTheme = queryClient.getQueryData<Theme>(['theme', { themeId: updatedTheme._id }]);

      queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => {
        if (!oldList) return [];
        return oldList.map(theme =>
          theme._id === updatedTheme._id
            ? {
                ...theme,
                name: updatedTheme.name,
                styles: updatedTheme.styles,
                updatedAt: new Date().toISOString()
              }
            : theme
        );
      });

      queryClient.setQueryData<Theme>(['theme', { themeId: updatedTheme._id }], oldTheme => ({
        ...(oldTheme as Theme),
        ...updatedTheme,
        updatedAt: new Date().toISOString()
      }));

      // Return for rollback
      return { previousList, previousTheme };
    },
    onError: (err, updatedTheme, context) => {
      // Rollback on error
      if (context?.previousList) {
        queryClient.setQueryData(['themes', 'list'], context.previousList);
      }
      if (context?.previousTheme) {
        queryClient.setQueryData(['theme', { themeId: updatedTheme._id }], context.previousTheme);
      }
      onError();
    },
    onSuccess: () => {
      onSuccess();
    },
    onSettled: async (_data, _error, { _id }) => {
      // Refetch to ensure server state
      await queryClient.invalidateQueries({
        queryKey: ['themes', 'list']
      });
      await queryClient.invalidateQueries({
        queryKey: ['theme', { themeId: _id }]
      });
    }
  });
}

export function useCreateTheme({ onSuccess, onError }: { onSuccess: (themeId: string) => void; onError: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (createThemeDto: CreateThemeDto) => createTheme(createThemeDto),
    onSuccess: ({ _id, name, updatedAt, styles, modifiedBy }) => {
      void queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => [
        { _id, name, updatedAt, styles, modifiedBy },
        ...(oldList ?? [])
      ]);
      onSuccess(_id);
    },
    onError
  });
}

export function useDuplicateTheme(
  id: string,
  { onSuccess, onError }: { onSuccess?: (themeId: string) => void; onError?: () => void }
) {
  const queryClient = useQueryClient();

  return useMutation<Theme>({
    mutationFn: async () => {
      await queryClient.cancelQueries({ queryKey: ['themes', 'list'] });
      const currentList = queryClient.getQueryData<ThemesData>(['themes', 'list']);
      if (!currentList) {
        return;
      }
      const currentItem = currentList.find(item => item._id === id);
      if (!currentItem) {
        return;
      }
      let newName = `Copy of ${currentItem.name}`;
      let index = 1;
      while (currentList.some(item => item.name === newName)) {
        newName = `Copy of ${currentItem.name} ${index}`;
        index++;
      }
      queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => {
        if (!oldList) return [];
        const currentIsoDate: string = new Date().toISOString();
        return [
          {
            _id: '',
            name: newName,
            updatedAt: currentIsoDate,
            styles: currentItem.styles,
            modifiedBy: { name: '', id: '' }
          },
          ...oldList
        ];
      });
      return duplicateTheme(id, newName);
    },
    onSuccess: ({ _id, name, updatedAt, styles, modifiedBy }) => {
      void queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => {
        if (!oldList) return [];
        return oldList.map(item => (item.name === name ? { _id, name, updatedAt, styles, modifiedBy } : item));
      });
      if (onSuccess) {
        onSuccess(_id);
      }
    },
    onError,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['themes', 'list'] });
    }
  });
}
